import React from "react";

const Projects = () => {
  return (
    <section id="projects" className="py-3">
      <div className="container">
        <h2 className="fw-bold mb-4">Projects</h2>

        {/* Benchmarks for Volatility-Managed Funds */}
        <div className="project mb-4 d-flex align-items-start">
          <div className="d-flex">
            <div className="line me-4 border-bottom border-5"></div> {/* Vertical Line */}
            <div className="w-100">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="fw-bold d-flex align-items-center w-100 justify-content-between">
                  Benchmarks for Volatility-Managed Funds
                  <a
                    href="https://www.hl.co.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center"
                    style={{ color: "#4C82F7", textDecoration: "none" }} // Blue color
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-arrow-up-right transition-transform duration-300 ease-in-out transform group-hover:translate-x-1"
                    >
                      <path d="M7 7h10v10"></path>
                      <path d="M7 17 17 7"></path>
                    </svg>
                  </a>
                </h3>
              </div>
              <p className="company-link">
                <a
                  href="https://www.hl.co.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="company-link-text"
                >
                  Industry Partner: Hargreaves Lansdown
                </a>
              </p>

              {/* Key Contributions */}
              <h6 className="fw-bold">• Key Contributions:</h6>
              <ul className="mt-3">
                <li>
                  Designed and implemented dynamic GARCH-based benchmarks for multi-asset funds, evaluating over 1,017 funds to identify optimal performance measures for volatility-managed strategies.
                </li>
                <li>
                  Built and analysed a Volatility-Managed (VM) Portfolio and a 60/40 static portfolio for comparative analysis, focusing on their performance relative to Morningstar benchmarks.
                </li>
                <li>
                  Conducted advanced performance assessments using tracking error, drawdown metrics, Sharpe ratio, and information ratios, providing actionable insights into benchmark accuracy and suitability
                </li>
                <li>
                  Demonstrated that traditional equity benchmarks fail to accurately assess low-risk, low-equity funds, recommending peer group benchmarks tailored to various equity allocation strategies.
                </li>
                <li>Automated data workflows and visualization processes using Python and Tableau, ensuring accurate and efficient communication of findings.</li>
              </ul>
              <div className="skills-list">
                <span className="skill-badge">Python(NumPy, pandas)</span>
                <span className="skill-badge">Tableau</span>
                <span className="skill-badge">STATA</span>
                <span className="skill-badge">Excel VBA</span>
                <span className="skill-badge">GARCH modeling</span>
                <span className="skill-badge">Regression analysis</span>
                <span className="skill-badge">Risk-adjusted metrics (Sharpe ratio, tracking error)</span>
                <span className="skill-badge">Matplotlib</span>
              </div>
            </div>
          </div>
        </div>

        {/* Portfolio Optimization */}
        <div className="project mb-4 d-flex align-items-start">
          <div className="d-flex">
            <div className="line me-4 border-bottom border-5"></div> {/* Vertical Line */}
            <div className="w-100">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="fw-bold d-flex align-items-center w-100 justify-content-between">
                  Portfolio Optimization
                  <a
                    href="https://www.exeter.ac.uk/departments/finance/aboutus/endowmentfunds/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center"
                    style={{ color: "#4C82F7", textDecoration: "none" }} // Blue color
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-arrow-up-right transition-transform duration-300 ease-in-out transform group-hover:translate-x-1"
                    >
                      <path d="M7 7h10v10"></path>
                      <path d="M7 17 17 7"></path>
                    </svg>
                  </a>
                </h3>
              </div>
              <p className="company-link">
                <a
                  href="https://www.exeter.ac.uk/departments/finance/aboutus/endowmentfunds/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="company-link-text"
                >
                  for the University of Exeter
                </a>
              </p>

              {/* Key Contributions */}
              <h6 className="fw-bold">• Key Contributions:</h6>
              <ul className="mt-3">
                <li>
                  Constructed optimal risky portfolios using Modern Portfolio Theory, achieving a 43.69% Sharpe ratio while maintaining a 95% VaR constraint for effective risk-adjusted returns.
                </li>
                <li>
                  Utilized Nelson-Siegel and Nelson-Siegel-Svensson models to estimate yield curves, with Sum of Squared Errors (SSE) of 0.001261 and 0.000282, respectively, highlighting model precision
                </li>
                <li>
                  Conducted 100 bootstrapped simulations to analyse portfolio resilience under market uncertainty, generating actionable insights through sensitivity analysis.
                </li>
                <li>
                  Delivered a complete portfolio allocation incorporating negatively correlated assets (80.02% risky assets, 19.98% bonds), achieving enhanced risk mitigation and superior performance.
                </li>
                <li>
                  Portfolio statistics revealed expected returns (E(R)) ranging from 1.52% to 2.21% and variance between 0.0015 and 0.0027, demonstrating diversified risk profiles.
                </li>
                <li>Visualized and communicated complex quantitative findings using Power BI and Excel Solver, supporting data-driven investment recommendations.</li>
              </ul>
                  <div className="skills-list">
                    <span className="skill-badge">Python</span>
                    <span className="skill-badge">Power BI</span>
                    <span className="skill-badge">Excel Solver</span>
                    <span className="skill-badge">Portfolio optimization</span>
                    <span className="skill-badge">VaR and CVaR modeling</span>
                    <span className="skill-badge">Yield curve estimation</span>
                    <span className="skill-badge">Monte Carlo simulations</span>
                    <span className="skill-badge">Sensitivity analysis</span>
                  </div>

            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Projects;
